import React from 'react';
import { Stack, TextField } from '@mui/material';
import { hstEmailError, hstUserIdError } from '@trackly/shared';
import { useHstAccessEditor } from '../../../../hooks/useHstAccessEditor';
import { EditorMode } from '../../../../types/editorTypes';

interface Props {
  mode: EditorMode;
}

type IdentityKey = 'hstUserId' | 'firstName' | 'lastName' | 'email';

interface IdentityField {
  key: IdentityKey;
  label: string;
  required?: boolean;
  validate?: (value?: string) => string | undefined;
  // Values that can never contain spaces drop the hard-to-see spaces at their ends as typed.
  trim?: boolean;
  // Shown under the field while it has no error; the field's text is selected on focus so typing replaces it.
  overridable?: boolean;
}

const HST_USER_ID_HINT = "Usually the person's email. Change it if their HST login is different.";

// FR-003/FR-004: messages are inline only and never block saving (FR-005).
const identityRows: IdentityField[][] = [
  [{ key: 'hstUserId', label: 'HST User ID', required: true, validate: hstUserIdError, trim: true, overridable: true }],
  [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
  ],
  [{ key: 'email', label: 'Email', required: true, validate: hstEmailError, trim: true }],
];

const inputValue = (field: IdentityField, value: string) => (field.trim ? value.trim() : value);

export const HstIdentityEditor: React.FC<Props> = ({ mode }) => {
  const { request, updateRequest } = useHstAccessEditor(mode);

  const renderField = (field: IdentityField) => {
    const { key, label, required, validate, overridable } = field;
    const value = request?.[key] ?? '';
    const error = validate?.(value);
    return (
      <TextField
        key={key}
        label={label}
        required={required}
        size="small"
        fullWidth
        value={value}
        onChange={(e) => updateRequest({ [key]: inputValue(field, e.target.value) })}
        onFocus={overridable ? (e) => e.target.select() : undefined}
        error={Boolean(error)}
        helperText={error ?? (overridable ? HST_USER_ID_HINT : undefined)}
      />
    );
  };

  return (
    <Stack spacing={2}>
      {identityRows.map((row) => (
        <Stack key={row[0].key} direction="row" gap={2}>
          {row.map(renderField)}
        </Stack>
      ))}
    </Stack>
  );
};

export default HstIdentityEditor;
